import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Input,
    message,
    Modal,
    Row,
    Radio,
    Switch,
    InputNumber
} from "antd";
import style from "./style.module.scss";
import {
    CourseListItem,
    deleteExamInfo,
    ExamListItem,
    ExamType,
    saveExamInfo,
    ExamTimeWay
} from "../../../../../service/exam";
import TimeRangePicker from "../TimeRangePicker";
import { errorMessage, isTrue } from "../../../../../service/service";
import DateRangePicker from "./conponent/DateRangePicker";
import moment from "moment";
import SelectPicker from "components/SelectPicker";

interface ModalAddProps {
    visible: boolean;
    courseList: CourseListItem[];
    onCancel: () => void;
    onOk: () => void;
    data?: ExamListItem;
}

export default function ModalAdd({
    visible,
    onCancel,
    onOk,
    data,
    courseList
}: ModalAddProps) {
    const [type, setType] = useState(data ? data.type : ExamType.Exam);
    const [name, setName] = useState(data ? data.name : "");
    const [timeRange, setTimeRange] = useState<[string, string]>(
        data ? [data.startTime, data.endTime] : ["", ""]
    ); //考试时间
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [seed, setSeed] = useState<BooleanValue>(
        data && data.seed ? data.seed : "1"
    ); //题目乱序
    const [optionSeed, setOptionSeed] = useState<BooleanValue>(
        data ? data.optionSeed : "1"
    ); //选项乱系
    const [courses, setCourses] = useState<CourseListItem[]>(
        data ? data.courseList : []
    );
    const [realtimeVideo, setRealtimeVideo] = useState(
        data ? isTrue(data.realtimeVideo) : false
    ); //实时监控
    const [catchPhoto, setCatchPhoto] = useState(
        data ? isTrue(data.catchPhoto) : false
    ); //抓拍

    const [examTimeWay, setExamTimeWay] = useState(
        data ? data.timeType : ExamTimeWay.unify
    ); //开考时间类型
    const [duration, setDuration] = useState(
        data ? Number(data.duration) : 120
    ); //有效时间
    const [error, setError] = useState(""); // 错误状态

    // 保存
    async function onSave() {
        if (!checkData()) return;

        if (courses.length === 0) {
            message.error("请选择课程");
            return;
        }

        if (name.trim().length === 0) {
            message.error("请填写考核名称");
            return;
        }

        if (timeRange[1] <= timeRange[0]) {
            message.error("考核结束时间需要大于起始时间");
            return;
        }

        if (courses.length > 1) {
            Modal.confirm({
                content: "是否多门课程使用同一套试卷？",
                onOk: () => save()
            });
            return;
        }

        save();
    }

    async function save() {
        setConfirmLoading(true);
        const params = {
            examId: data ? data.examId : undefined,
            type,
            name,
            courses,
            startTime: timeRange[0],
            endTime: timeRange[1],
            seed,
            optionSeed,
            timeType: examTimeWay,
            duration,
            realtimeVideo,
            catchPhoto
        };

        try {
            await saveExamInfo(params);
            onOk();
            message.info("保存成功");
        } catch (e) {
            message.error(errorMessage(e));
        }

        setConfirmLoading(false);
    }

    // 删除
    async function onDelete() {
        if (!data) return;

        const result = await new Promise(resolve => {
            Modal.confirm({
                content: "确定要删除此次考核吗?",
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });

        if (!result) return;

        try {
            await deleteExamInfo(data.examId);
            onOk();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    function onCourseListChange(value: string[]) {
        setCourses(
            courseList.filter(item => value.includes(getCourseUniqueId(item)))
        );
    }

    // 检查数据
    function checkData(): boolean {
        if (
            type === ExamType.Exam &&
            (timeRange[0]?.trim().length === 0 ||
                timeRange[1]?.trim().length === 0)
        ) {
            const newTimeError =
                examTimeWay === ExamTimeWay.unify
                    ? "考核有效期必填"
                    : "有效期必填";
            setError(newTimeError);
            return false;
        }

        if (
            type === ExamType.Exam &&
            !moment(timeRange[0]).isBefore(timeRange[1])
        ) {
            setError("开始时间必须大于结束时间");
            return false;
        }
        return true;
    }

    useEffect(() => {
        if (
            timeRange[0]?.trim().length !== 0 ||
            timeRange[1]?.trim().length !== 0
        )
            setError("");
    }, [timeRange]);

    // 生成课程的唯一主键
    function getCourseUniqueId(item: CourseListItem) {
        return `${item.courseNo}${item.courseSeq}`;
    }

    function courseLabel(item: CourseListItem) {
        return `[${item.courseNo}-${item.courseSeq}]${item.courseName}`;
    }

    const optionsData = courseList.map(item => ({
        key: getCourseUniqueId(item),
        value: getCourseUniqueId(item),
        label: courseLabel(item)
    }));

    return (
        <Modal
            title={data ? "编辑考核" : "新增考核"}
            visible={visible}
            onCancel={onCancel}
            maskClosable={false}
            confirmLoading={confirmLoading}
            className={style.root}
            width={610}
            footer={[
                <Button
                    key="delete"
                    type="danger"
                    onClick={onDelete}
                    style={{
                        float: "left",
                        display: data ? "inline-block" : "none"
                    }}
                    disabled={
                        isTrue(data?.isPublish) || isTrue(data?.studentDo)
                    }
                    title={
                        data && isTrue(data.studentDo)
                            ? "已经有学员提交了试卷"
                            : ""
                    }>
                    删除
                </Button>,
                <Button key="back" onClick={onCancel}>
                    取消
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={confirmLoading}
                    onClick={onSave}>
                    确定
                </Button>
            ]}>
            <Row className={style.row}>
                <Col span={7} className={style.label}>
                    考核名称：
                </Col>
                <Col span={14}>
                    <Input
                        style={{ width: "100%" }}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        maxLength={140}
                        placeholder="请输入考核名称"
                        disabled={isTrue(data?.isPublish)}
                    />
                </Col>
            </Row>

            <Row className={style.row}>
                <Col span={7} className={style.label}>
                    课程名称：
                </Col>
                <Col span={14}>
                    <SelectPicker
                        initVal={courses.map(item => getCourseUniqueId(item))}
                        onChange={onCourseListChange}
                        optionsData={optionsData}
                        placeholder="请选择课程"
                        maxTagCount={2}
                        style={{ width: "100%" }}
                        allowClear
                        disabled={isTrue(data?.isPublish)}
                    />
                </Col>
            </Row>

            <Row className={style.row}>
                <Col span={7} className={style.label}>
                    考试方式：
                </Col>
                <Col span={14}>
                    <Radio.Group
                        value={type}
                        onChange={e => setType(e.target.value)}
                        disabled={!!data}>
                        <Radio value={ExamType.Exam}>线上考试</Radio>
                        <Radio value={ExamType.Thesis}>论文考核</Radio>
                        <Radio value={ExamType.Oral}>口试</Radio>
                    </Radio.Group>
                </Col>
            </Row>

            {type === ExamType.Exam && (
                <div>
                    <Row className={style.row}>
                        <Col span={7} className={style.label}>
                            开考时间：
                        </Col>
                        <Col span={14}>
                            <Radio.Group
                                value={examTimeWay}
                                onChange={e => setExamTimeWay(e.target.value)}
                                disabled={
                                    isTrue(data?.isPublish) ||
                                    isTrue(data?.studentDo)
                                }>
                                <Radio value="1">统一时间</Radio>
                                <Radio value="2">学生自主安排</Radio>
                            </Radio.Group>
                        </Col>
                        <Col span={14}>
                            {examTimeWay === ExamTimeWay.free && (
                                <div className={style.timeRange}>
                                    考试时限:&nbsp;&nbsp;
                                    <InputNumber
                                        min={1}
                                        value={duration}
                                        onChange={value =>
                                            setDuration(value || 120)
                                        }
                                        max={1440}
                                        defaultValue={120}
                                        disabled={
                                            isTrue(data?.isPublish) ||
                                            isTrue(data?.studentDo)
                                        }
                                    />
                                    &nbsp;&nbsp; 分钟
                                </div>
                            )}
                        </Col>
                    </Row>

                    <div className={style.instructions}>
                        <div>使用说明：</div>
                        <div>
                            1.“统一时间”是指在“考试有效期限”内（最多 24
                            小时），考生越晚进入答题，剩余的时长越短；如设置为下午1点到3点，当考生2点进入考试，则只剩下1个小时答题时长。
                        </div>
                        <div>
                            2.“学生自主安排”是指在有效期限内，考生无论何时开始答题，都不影响其作答时长，“考试时限”最小为
                            0 最大为 1440 （分钟） 即 24 小时。
                        </div>
                    </div>

                    <Row className={style.row}>
                        <Col span={7} className={style.label}>
                            <i className={style.warning}>*</i>
                            {examTimeWay === ExamTimeWay.unify
                                ? "考试有效期限："
                                : "有效期："}
                        </Col>
                        <DateRangePicker
                            examTimeWay={examTimeWay}
                            start={data?.startTime ? timeRange[0] : ""}
                            end={data?.endTime ? timeRange[1] : ""}
                            onChange={setTimeRange}
                            disabled={
                                isTrue(data?.isPublish) ||
                                isTrue(data?.studentDo)
                            }
                        />
                        <Col span={14}>
                            {error && (
                                <div className={style.error}>{error}</div>
                            )}
                        </Col>
                    </Row>

                    <Row className={style.row}>
                        <Col span={7} className={style.label}>
                            题目乱序：
                        </Col>
                        <Col span={14}>
                            <Radio.Group
                                value={seed}
                                onChange={e => setSeed(e.target.value)}
                                disabled={
                                    isTrue(data?.isPublish) ||
                                    isTrue(data?.studentDo)
                                }>
                                <Radio value={"1"}>是</Radio>
                                <Radio value={"0"}>否</Radio>
                            </Radio.Group>
                        </Col>
                    </Row>

                    <Row className={style.row}>
                        <Col span={7} className={style.label}>
                            选项乱序：
                        </Col>
                        <Col span={14}>
                            <Radio.Group
                                value={optionSeed}
                                onChange={e => setOptionSeed(e.target.value)}
                                disabled={
                                    isTrue(data?.isPublish) ||
                                    isTrue(data?.studentDo)
                                }>
                                <Radio value={"1"}>是</Radio>
                                <Radio value={"0"}>否</Radio>
                            </Radio.Group>
                        </Col>
                    </Row>

                    <Row className={style.row}>
                        <Col span={7} className={style.label}>
                            实时监控：
                        </Col>
                        <Col span={14}>
                            <Switch
                                size="small"
                                checked={realtimeVideo}
                                onChange={() =>
                                    setRealtimeVideo(!realtimeVideo)
                                }
                            />
                        </Col>
                    </Row>
                    <div className={style.instructions}>
                        说明：仅支持pc端，且电脑摄像头正常开启的考生能看到摄像头采集的内容
                    </div>

                    <Row className={style.row}>
                        <Col span={7} className={style.label}>
                            摄像头抓拍：
                        </Col>
                        <Col span={14}>
                            <Switch
                                size="small"
                                checked={catchPhoto}
                                onChange={() => setCatchPhoto(!catchPhoto)}
                                disabled={
                                    isTrue(data?.isPublish) ||
                                    isTrue(data?.studentDo)
                                }
                            />
                        </Col>
                    </Row>
                    <div className={style.instructions}>
                        说明：仅支持pc端，且电脑摄像头正常开启的才能正常抓拍
                    </div>

                    {/*<Row className={style.row}>*/}
                    {/*    <Col span={7} className={style.label}>*/}
                    {/*        屏幕抓拍：*/}
                    {/*    </Col>*/}
                    {/*    <Col span={14}>*/}
                    {/*        <Switch*/}
                    {/*            size="small"*/}
                    {/*            checked={catchScreen}*/}
                    {/*            onChange={() => setCatchScreen(!catchScreen)}*/}
                    {/*            disabled={*/}
                    {/*                isTrue(data?.isPublish) ||*/}
                    {/*                isTrue(data?.studentDo)*/}
                    {/*            }*/}
                    {/*        />*/}
                    {/*    </Col>*/}
                    {/*</Row>*/}
                    {/*<div className={style.instructions}>*/}
                    {/*    说明：PC端和移动都支持，仅支持抓拍浏览器当前界面内容*/}
                    {/*</div>*/}
                </div>
            )}

            {type !== ExamType.Exam && (
                <Row className={style.row}>
                    <Col span={7} className={style.label}>
                        考试时间：
                    </Col>
                    <TimeRangePicker
                        type={type}
                        start={timeRange[0]}
                        end={timeRange[1]}
                        onChange={setTimeRange}
                    />
                </Row>
            )}
        </Modal>
    );
}
